// /var/www/aesirerp_lp/frontend/src/components/ecosystem/DataPipeline.tsx
'use client';

import Image from 'next/image';
import { 
  Globe, Instagram, MessageCircle, MapPin, 
  DollarSign, Briefcase, Box, Database
} from 'lucide-react';
import styles from './DataPipeline.module.css';

export function DataPipeline() {
  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <span className={styles.badge}>O Fluxo Invisível</span>
        <h2 className={styles.title}>
          De ponta a ponta, sem intervenção humana.
        </h2>
      </div>

      <div className={styles.stage}>
        
        {/* ==========================================
            A MAGIA SVG (LINHAS E VIAJANTES COM ÍCONES)
            ========================================== */}
        <svg viewBox="0 0 1000 500" className={styles.svgLines} preserveAspectRatio="xMidYMid meet">
          <defs>
            {/* Filtros de Brilho */}
            <filter id="glowOrange" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
            <filter id="glowGreen" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>

            {/* Traçados das Linhas */}
            <path id="pathL1" d="M 150 100 C 300 100, 300 250, 430 250" className={styles.drawPath} />
            <path id="pathL2" d="M 150 200 C 300 200, 300 250, 430 250" className={styles.drawPath} />
            <path id="pathL3" d="M 150 300 C 300 300, 300 250, 430 250" className={styles.drawPath} />
            <path id="pathL4" d="M 150 400 C 300 400, 300 250, 430 250" className={styles.drawPath} />

            <path id="pathR1" d="M 570 250 C 700 250, 700 100, 850 100" className={styles.drawPath} />
            <path id="pathR2" d="M 570 250 C 700 250, 700 200, 850 200" className={styles.drawPath} />
            <path id="pathR3" d="M 570 250 C 700 250, 700 300, 850 300" className={styles.drawPath} />
            <path id="pathR4" d="M 570 250 C 700 250, 700 400, 850 400" className={styles.drawPath} />
          </defs>

          {/* Renderizar as linhas */}
          <use href="#pathL1"/><use href="#pathL2"/><use href="#pathL3"/><use href="#pathL4"/>
          <use href="#pathR1"/><use href="#pathR2"/><use href="#pathR3"/><use href="#pathR4"/>

          {/* VIAJANTE 1: Alerta / Lead (Laranja) */}
          <g>
            <circle r="12" fill="white" stroke="#ea580c" strokeWidth="2" />
            <path d="M0 -5 L-5 4 L5 4 Z" fill="#ea580c" /> {/* Triângulo de Alerta */}
            <animateMotion dur="4s" repeatCount="indefinite" begin="0s"><mpath href="#pathL1"/></animateMotion>
            <animate attributeName="opacity" values="0; 1; 1; 0" keyTimes="0; 0.1; 0.9; 1" dur="4s" repeatCount="indefinite" begin="0s" />
          </g>

          {/* VIAJANTE 2: Evento Normal (Azul) */}
          <g>
            <circle r="10" fill="white" stroke="#3b82f6" strokeWidth="2" />
            <circle r="3" fill="#3b82f6" /> {/* Ponto Azul */}
            <animateMotion dur="3.5s" repeatCount="indefinite" begin="1s"><mpath href="#pathL2"/></animateMotion>
            <animate attributeName="opacity" values="0; 1; 1; 0" keyTimes="0; 0.1; 0.9; 1" dur="3.5s" repeatCount="indefinite" begin="1s" />
          </g>

          {/* VIAJANTE 3: Mensagem (Laranja) */}
          <g>
            <circle r="12" fill="white" stroke="#ea580c" strokeWidth="2" />
            <path d="M0 -5 L-5 4 L5 4 Z" fill="#ea580c" />
            <animateMotion dur="3.2s" repeatCount="indefinite" begin="0.5s"><mpath href="#pathL3"/></animateMotion>
            <animate attributeName="opacity" values="0; 1; 1; 0" keyTimes="0; 0.1; 0.9; 1" dur="3.2s" repeatCount="indefinite" begin="0.5s" />
          </g>

          {/* VIAJANTE 4: Saída Financeira (Verde Brilhante com Check) */}
          <g>
            <circle r="14" fill="white" stroke="#10b981" strokeWidth="2" filter="url(#glowGreen)" />
            <path d="M-4 0 L-1 3 L5 -3" stroke="#10b981" strokeWidth="2" fill="none" /> {/* Ícone Check */}
            <animateMotion dur="3.5s" repeatCount="indefinite" begin="1s"><mpath href="#pathR1"/></animateMotion>
            <animate attributeName="opacity" values="0; 1; 1; 0" keyTimes="0; 0.1; 0.9; 1" dur="3.5s" repeatCount="indefinite" begin="1s" />
          </g>

          {/* VIAJANTE 5: Saída Evento Concluído */}
          <g>
            <circle r="12" fill="white" stroke="#10b981" strokeWidth="2" />
            <circle r="4" fill="#10b981" />
            <animateMotion dur="3s" repeatCount="indefinite" begin="0.5s"><mpath href="#pathR2"/></animateMotion>
            <animate attributeName="opacity" values="0; 1; 1; 0" keyTimes="0; 0.1; 0.9; 1" dur="3s" repeatCount="indefinite" begin="0.5s" />
          </g>

          {/* VIAJANTE 6: Saída CRM (Azul) */}
          <g>
            <circle r="12" fill="white" stroke="#3b82f6" strokeWidth="2" />
            <path d="M-4 0 L-1 3 L5 -3" stroke="#3b82f6" strokeWidth="2" fill="none" />
            <animateMotion dur="4s" repeatCount="indefinite" begin="2s"><mpath href="#pathR3"/></animateMotion>
            <animate attributeName="opacity" values="0; 1; 1; 0" keyTimes="0; 0.1; 0.9; 1" dur="4s" repeatCount="indefinite" begin="2s" />
          </g>
          
          {/* VIAJANTE 7: Saída Estoque */}
          <g>
            <circle r="12" fill="white" stroke="#10b981" strokeWidth="2" />
            <path d="M-4 0 L-1 3 L5 -3" stroke="#10b981" strokeWidth="2" fill="none" />
            <animateMotion dur="3.2s" repeatCount="indefinite" begin="1.5s"><mpath href="#pathR4"/></animateMotion>
            <animate attributeName="opacity" values="0; 1; 1; 0" keyTimes="0; 0.1; 0.9; 1" dur="3.2s" repeatCount="indefinite" begin="1.5s" />
          </g>
        </svg>

        {/* ==========================================
            HTML SOBREPOSTO (OS NÓS VISUAIS)
            ========================================== */}
        
        {/* Entradas */}
        <div className={`${styles.nodePill} ${styles.l1}`}>
          <div className={styles.nodeIcon}><Globe size={16} color="#64748b" /></div> Loja Virtual
        </div>
        <div className={`${styles.nodePill} ${styles.l2}`}>
          <div className={styles.nodeIcon}><Instagram size={16} color="#E1306C" /></div> Instagram
        </div>
        <div className={`${styles.nodePill} ${styles.l3}`}>
          <div className={styles.nodeIcon}><MessageCircle size={16} color="#25D366" /></div> WhatsApp
        </div>
        <div className={`${styles.nodePill} ${styles.l4}`}>
          <div className={styles.nodeIcon}><MapPin size={16} color="#ea580c" /></div> Buscador B2B
        </div>

        {/* Hub Central */}
        <div className={styles.hubBrain}>
          <Image src="/assets/images/logo.png" alt="Aesir Logo" width={48} height={48} />
          <h3>Aesir Motor</h3>
        </div>

        {/* Saídas (Com as classes de Chacoalhada sincronizadas) */}
        <div className={`${styles.nodePill} ${styles.r1} ${styles.bumpR1}`}>
          <div className={styles.nodeIcon}><DollarSign size={16} color="#10b981" /></div> Financeiro (NFe)
        </div>
        <div className={`${styles.nodePill} ${styles.r2} ${styles.bumpR2}`}>
          <div className={styles.nodeIcon}><Briefcase size={16} color="#10b981" /></div> RH & Equipe
        </div>
        <div className={`${styles.nodePill} ${styles.r3} ${styles.bumpR3}`}>
          <div className={styles.nodeIcon}><Database size={16} color="#3b82f6" /></div> CRM Pipeline
        </div>
        <div className={`${styles.nodePill} ${styles.r4} ${styles.bumpR4}`}>
          <div className={styles.nodeIcon}><Box size={16} color="#10b981" /></div> Estoque Multi-CD
        </div>

      </div>
    </section>
  );
}